import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

import { FieldError } from "@/components/ui/field-error";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useStockCosting } from "@/hooks/use-stock-costing";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { Package, ChevronDown, ArrowRight, Info, CheckSquare, Square, Plus, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { mockLocations, mockWarehouses } from "@/data/mockWarehousesLocations";
import { processTransfer as processLocationTransfer } from "@/services/location-stock-service";
import { getStockItems } from "@/services/stockDataService";
import { StockItem } from "@/types/stock-item";

// Get all stock items
const allStockItems = getStockItems();

// Use real locations from warehouse data
const locations = mockLocations.filter(l => l.active).map(l => l.locationCode);

interface TransferItem {
  stockCode: string;
  stockDescription: string;
  availableQty: number;
  unitPrice: number;
  selected: boolean;
  transferQty: string;
  sourceWarehouse: string;
  sourceLocation: string;
  destinationWarehouse: string;
  destinationLocation: string;
  errors: {
    transferQty?: string;
    destinationWarehouse?: string;
    destinationLocation?: string;
  };
}

interface FormData {
  referenceNumber: string;
  transferDate: Date;
  sourceWarehouse: string;
  sourceLocation: string;
  destinationDefaultWarehouse: string;
  destinationDefaultLocation: string;
  notes: string;
}

interface FormErrors {
  referenceNumber?: string;
  transferDate?: string;
  sourceWarehouse?: string;
  sourceLocation?: string;
  notes?: string;
}

export function TransferMovementPage() {
  const { navigateTo } = useNavigation();
  
  // Redirect to unified stock movement page with transfer type pre-selected
  useEffect(() => {
    navigateTo("add-movement", { type: "Transfer Between Locations" });
  }, [navigateTo]);
  
  const { valuationMethod, getValuationMethodDisplayName } = useStockCosting();
  
  const [formData, setFormData] = useState<FormData>({
    referenceNumber: "",
    transferDate: new Date(),
    sourceWarehouse: "",
    sourceLocation: "",
    destinationDefaultWarehouse: "",
    destinationDefaultLocation: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [transferItems, setTransferItems] = useState<TransferItem[]>([]);
  const [availableItems, setAvailableItems] = useState<TransferItem[]>([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [tempSelectedItems, setTempSelectedItems] = useState<Set<string>>(new Set());
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [transferMode, setTransferMode] = useState<"warehouse" | "location">("warehouse");
  const [sourceWarehouse, setSourceWarehouse] = useState("");
  const referenceInputRef = useRef<HTMLInputElement>(null);

  // Prepare warehouse options for combobox
  const warehouseOptions: ComboboxOption[] = mockWarehouses
    .filter(w => w.active)
    .map(w => ({
      value: w.code,
      label: w.name,
      description: w.code,
    }));

  // Focus on reference number field on mount
  useEffect(() => {
    referenceInputRef.current?.focus();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    const hasData =
      formData.referenceNumber ||
      formData.sourceLocation ||
      formData.notes ||
      transferItems.length > 0;
    setHasUnsavedChanges(hasData);
  }, [formData, transferItems]);

  // Load items when source warehouse or location changes
  useEffect(() => {
    if (transferMode === "warehouse" && formData.sourceWarehouse) {
      loadItemsForWarehouse(formData.sourceWarehouse);
    } else if (transferMode === "location" && formData.sourceLocation) {
      loadItemsForLocation(formData.sourceLocation);
    } else {
      setAvailableItems([]);
    }
  }, [formData.sourceWarehouse, formData.sourceLocation, transferMode]);

  const loadItemsForWarehouse = async (warehouseCode: string) => {
    setIsLoadingItems(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Get all stock items from the selected warehouse
    const warehouseItems = allStockItems.filter(
      (item) => item.warehouseCode === warehouseCode && item.physicalQuantity > 0
    );

    // Group items by stock code and sum quantities across locations
    const itemMap = new Map<string, { item: StockItem; totalQty: number; locations: string[] }>();
    
    warehouseItems.forEach((item) => {
      const existing = itemMap.get(item.code);
      if (existing) {
        existing.totalQty += item.physicalQuantity;
        existing.locations.push(item.locationCode);
      } else {
        itemMap.set(item.code, {
          item,
          totalQty: item.physicalQuantity,
          locations: [item.locationCode],
        });
      }
    });

    // Convert to TransferItem format
    const items: TransferItem[] = Array.from(itemMap.values()).map(({ item, totalQty }) => ({
      stockCode: item.code,
      stockDescription: item.description,
      availableQty: totalQty,
      unitPrice: item.unitPrice,
      selected: false,
      transferQty: "",
      sourceWarehouse: warehouseCode,
      sourceLocation: "",
      destinationWarehouse: "",
      destinationLocation: "",
      errors: {},
    }));

    setAvailableItems(items);
    setIsLoadingItems(false);

    if (items.length === 0) {
      const warehouseName = mockWarehouses.find(w => w.code === warehouseCode)?.name || warehouseCode;
      toast.info(`No items with stock found in ${warehouseName}`);
    } else {
      const warehouseName = mockWarehouses.find(w => w.code === warehouseCode)?.name || warehouseCode;
      toast.success(`Loaded ${items.length} items from ${warehouseName}`);
    }
  };

  const loadItemsForLocation = async (locationCode: string) => {
    setIsLoadingItems(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Get all stock items from the selected location
    const locationItems = allStockItems.filter(
      (item) => item.locationCode === locationCode && item.physicalQuantity > 0
    );

    // Convert to TransferItem format
    const items: TransferItem[] = locationItems.map((item) => ({
      stockCode: item.code,
      stockDescription: item.description,
      availableQty: item.physicalQuantity,
      unitPrice: item.unitPrice,
      selected: false,
      transferQty: "",
      sourceWarehouse: item.warehouseCode,
      sourceLocation: locationCode,
      destinationWarehouse: "",
      destinationLocation: "",
      errors: {},
    }));

    setAvailableItems(items);
    setIsLoadingItems(false);

    if (items.length === 0) {
      toast.info(`No items with stock found in location ${locationCode}`);
    } else {
      toast.success(`Loaded ${items.length} items from location ${locationCode}`);
    }
  };

  const handleOpenItemModal = () => {
    // Initialize temp selection with currently selected items
    const currentlySelected = new Set(transferItems.map(item => item.stockCode));
    setTempSelectedItems(currentlySelected);
    setItemSearchQuery("");
    setShowItemModal(true);
  };

  const handleModalItemToggle = (stockCode: string) => {
    setTempSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stockCode)) {
        newSet.delete(stockCode);
      } else {
        newSet.add(stockCode);
      }
      return newSet;
    });
  };

  const handleModalSelectAll = () => {
    const filtered = getFilteredAvailableItems();
    const allSelected = filtered.every(item => tempSelectedItems.has(item.stockCode));
    
    if (allSelected) {
      // Deselect all filtered items
      setTempSelectedItems(prev => {
        const newSet = new Set(prev);
        filtered.forEach(item => newSet.delete(item.stockCode));
        return newSet;
      });
    } else {
      // Select all filtered items
      setTempSelectedItems(prev => {
        const newSet = new Set(prev);
        filtered.forEach(item => newSet.add(item.stockCode));
        return newSet;
      });
    }
  };

  const handleConfirmItemSelection = () => {
    // Get the selected items from available items
    const selectedItems = availableItems.filter(item => 
      tempSelectedItems.has(item.stockCode)
    );

    // Merge with existing transfer items, preserving their data
    const existingItemsMap = new Map(
      transferItems.map(item => [item.stockCode, item])
    );

    const updatedItems = selectedItems.map(item => {
      const existing = existingItemsMap.get(item.stockCode);
      if (existing) {
        // Keep existing data for items already in the list
        return existing;
      } else {
        // New item - initialize with defaults
        if (transferMode === "warehouse") {
          return {
            ...item,
            selected: true,
            transferQty: "",
            destinationWarehouse: formData.destinationDefaultWarehouse || "",
            destinationLocation: "",
            errors: {},
          };
        } else {
          return {
            ...item,
            selected: true,
            transferQty: "",
            destinationWarehouse: "",
            destinationLocation: formData.destinationDefaultLocation || "",
            errors: {},
          };
        }
      }
    });

    setTransferItems(updatedItems);
    setShowItemModal(false);
    
    const newItemsCount = updatedItems.length - transferItems.length;
    if (newItemsCount > 0) {
      toast.success(`Added ${newItemsCount} item(s) to transfer`);
    }
  };

  const getFilteredAvailableItems = () => {
    if (!itemSearchQuery.trim()) {
      return availableItems;
    }
    
    const query = itemSearchQuery.toLowerCase();
    return availableItems.filter(item =>
      item.stockCode.toLowerCase().includes(query) ||
      item.stockDescription.toLowerCase().includes(query)
    );
  };



  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate summary totals
  const calculateSummary = () => {
    const totalItems = transferItems.length;
    const totalQuantity = transferItems.reduce(
      (sum, item) => sum + (parseFloat(item.transferQty) || 0),
      0
    );
    const totalValue = transferItems.reduce(
      (sum, item) => sum + (parseFloat(item.transferQty) || 0) * item.unitPrice,
      0
    );
    
    // Get unique destination locations/warehouses based on mode
    const destinations = new Set(
      transferItems
        .filter(item => transferMode === "warehouse" ? item.destinationWarehouse : item.destinationLocation)
        .map(item => transferMode === "warehouse" ? item.destinationWarehouse : item.destinationLocation)
    );

    return { totalItems, totalQuantity, totalValue, destinations: Array.from(destinations) };
  };

  // Remove item from transfer list
  const handleRemoveItem = (stockCode: string) => {
    setTransferItems((prev) => prev.filter(item => item.stockCode !== stockCode));
    toast.success("Item removed from transfer");
  };

  // Handle item field changes
  const handleItemChange = (
    stockCode: string,
    field: keyof TransferItem,
    value: string
  ) => {
    setTransferItems((prev) =>
      prev.map((item) => {
        if (item.stockCode !== stockCode) return item;

        const updatedItem = { ...item, [field]: value };

        // Clear error for this field
        if (updatedItem.errors[field as keyof TransferItem["errors"]]) {
          updatedItem.errors = {
            ...updatedItem.errors,
            [field]: undefined,
          };
        }

        return updatedItem;
      })
    );
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    // Validate reference number
    if (!formData.referenceNumber.trim()) {
      errors.referenceNumber = "Transfer reference is required";
      isValid = false;
    }

    // Validate transfer date
    if (!formData.transferDate) {
      errors.transferDate = "Transfer date is required";
      isValid = false;
    } else if (formData.transferDate > new Date()) {
      errors.transferDate = "Transfer date cannot be in the future";
      isValid = false;
    }

    // Validate source warehouse/location based on mode
    if (transferMode === "warehouse") {
      if (!formData.sourceWarehouse) {
        errors.sourceWarehouse = "Source warehouse is required";
        isValid = false;
      }
    } else {
      if (!formData.sourceLocation) {
        errors.sourceLocation = "Source location is required";
        isValid = false;
      }
    }

    // Validate notes length
    if (formData.notes.length > 500) {
      errors.notes = "Notes must be 500 characters or less";
      isValid = false;
    }

    setFormErrors(errors);

    // Check for at least one item in transfer list
    if (transferItems.length === 0) {
      toast.error("Please add at least one item to transfer");
      isValid = false;
    }

    // Validate transfer items
    const updatedItems = transferItems.map((item) => {
      const itemErrors: TransferItem["errors"] = {};

      // Validate transfer quantity
      const qty = parseFloat(item.transferQty);
      if (!item.transferQty || isNaN(qty) || qty <= 0) {
        itemErrors.transferQty = "Quantity required";
        isValid = false;
      } else if (qty > item.availableQty) {
        itemErrors.transferQty = `Max ${item.availableQty}`;
        isValid = false;
      }

      // Validate destination based on mode
      if (transferMode === "warehouse") {
        if (!item.destinationWarehouse) {
          itemErrors.destinationWarehouse = "Destination required";
          isValid = false;
        } else if (item.destinationWarehouse === formData.sourceWarehouse) {
          itemErrors.destinationWarehouse = "Cannot be same as source";
          isValid = false;
        }
      } else {
        if (!item.destinationLocation) {
          itemErrors.destinationLocation = "Destination required";
          isValid = false;
        } else if (item.destinationLocation === formData.sourceLocation) {
          itemErrors.destinationLocation = "Cannot be same as source";
          isValid = false;
        }
      }

      return { ...item, errors: itemErrors };
    });

    setTransferItems(updatedItems);

    return isValid;
  };

  // Handle form field changes
  const handleFieldChange = (field: keyof FormData, value: string | Date) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      navigateTo("stock-movements");
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('[aria-invalid="true"]');
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      (firstError as HTMLElement)?.focus();

      toast.error("Please fix all errors before saving");
      return;
    }

    setIsSaving(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    console.log(`🔄 Processing transfer using ${getValuationMethodDisplayName()}`);
    console.log(`  From: ${formData.sourceLocation}`);

    // Create paired movement records (OUT + IN) for each transfer item
    const movements: any[] = [];

    transferItems.forEach((item) => {
      const qty = parseFloat(item.transferQty);
      const lineTotal = qty * item.unitPrice;
      
      console.log(`  ✅ Transferring: ${item.stockDescription}`);
      console.log(`     ${formData.sourceLocation} → ${item.destinationLocation}`);
      console.log(`     Qty: ${qty}, Value: ${formatCurrency(lineTotal)}`);
      
      const baseId = `MOV${Date.now()}-${item.stockCode}`;

      // Outbound movement (negative quantity)
      movements.push({
        id: `${baseId}-OUT`,
        reference: `${formData.referenceNumber} - OUT`,
        date: formData.transferDate,
        type: "Transfer",
        source: "Manual",
        stockCode: item.stockCode,
        stockDescription: item.stockDescription,
        location: formData.sourceLocation,
        quantity: -qty,
        unitPrice: item.unitPrice,
        lineTotal: -lineTotal,
        userName: "John Smith",
        notes: formData.notes || null,
        transferReference: formData.referenceNumber,
      });

      // Inbound movement (positive quantity)
      movements.push({
        id: `${baseId}-IN`,
        reference: `${formData.referenceNumber} - IN`,
        date: formData.transferDate,
        type: "Transfer",
        source: "Manual",
        stockCode: item.stockCode,
        stockDescription: item.stockDescription,
        location: item.destinationLocation,
        quantity: qty,
        unitPrice: item.unitPrice,
        lineTotal: lineTotal,
        userName: "John Smith",
        notes: formData.notes || null,
        transferReference: formData.referenceNumber,
      });

      // Update location stock levels
      const transferSuccess = processLocationTransfer(
        formData.sourceLocation,
        item.destinationLocation,
        qty
      );
      if (transferSuccess) {
        console.log(`✅ Location stock updated: ${formData.sourceLocation} → ${item.destinationLocation} (${qty} units)`);
      }
    });

    console.log("Created transfer movements:", movements);

    setIsSaving(false);
    const summary = calculateSummary();
    toast.success(
      `Transfer completed successfully - ${summary.totalItems} item${
        summary.totalItems > 1 ? "s" : ""
      } moved from ${formData.sourceLocation}`
    );
    navigateTo("stock-movements");
  };

  const summary = calculateSummary();

  return (
    <div className="space-y-6 pb-24">
      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">
            Transfer Between Locations
          </h1>
          <Badge className="bg-orange-500 hover:bg-orange-600">Transfer</Badge>
          <Badge 
            variant="outline" 
            className={valuationMethod === 'FIFO' 
              ? "bg-purple-50 text-purple-700 border-purple-200" 
              : "bg-green-50 text-green-700 border-green-200"}
          >
            {getValuationMethodDisplayName()}
          </Badge>
        </div>
        <p className="text-muted-foreground">
          Select source location, choose items, and specify destinations
        </p>
      </div>

      {/* Valuation Method Info Alert */}
      {valuationMethod === 'fifo' && (
        <Alert className="bg-purple-50 border-purple-200">
          <Info className="h-4 w-4 text-purple-600" />
          <AlertDescription className="text-purple-900">
            <strong>FIFO Mode Active:</strong> Transfers maintain cost layer integrity. 
            Cost layers are moved from source to destination location.
          </AlertDescription>
        </Alert>
      )}

      {/* Transfer Header Section */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="referenceNumber">
              Transfer Reference <span className="text-destructive">*</span>
            </Label>
            <Input
              ref={referenceInputRef}
              id="referenceNumber"
              type="text"
              placeholder="e.g., TR-2024-001"
              maxLength={50}
              value={formData.referenceNumber}
              onChange={(e) =>
                handleFieldChange("referenceNumber", e.target.value)
              }
              aria-required="true"
              aria-invalid={!!formErrors.referenceNumber}
              aria-describedby={
                formErrors.referenceNumber
                  ? "referenceNumber-error"
                  : undefined
              }
              className={cn(formErrors.referenceNumber && "border-destructive")}
            />
            {formErrors.referenceNumber && (
              <FieldError
                id="referenceNumber-error"
                message={formErrors.referenceNumber}
              />
            )}
          </div>

          {/* Transfer Date */}
          <div className="space-y-2">
            <Label htmlFor="transferDate">
              Transfer Date <span className="text-destructive">*</span>
            </Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="transferDate"
                  variant="outline"
                  className={cn(
                    "w-full justify-between font-normal",
                    !formData.transferDate && "text-muted-foreground",
                    formErrors.transferDate && "border-destructive"
                  )}
                  aria-required="true"
                  aria-invalid={!!formErrors.transferDate}
                  aria-describedby={
                    formErrors.transferDate ? "transferDate-error" : undefined
                  }
                >
                  {formData.transferDate
                    ? new Intl.DateTimeFormat("en-GB").format(
                        formData.transferDate
                      )
                    : "Select date"}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.transferDate}
                  onSelect={(date) => {
                    if (date) {
                      handleFieldChange("transferDate", date);
                      setDatePickerOpen(false);
                    }
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {formErrors.transferDate && (
              <FieldError
                id="transferDate-error"
                message={formErrors.transferDate}
              />
            )}
          </div>

          {/* Transfer Mode Toggle - Full Width */}
          <div className="space-y-3 md:col-span-2 pb-4 border-b">
            <Label>Transfer Mode</Label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setTransferMode("warehouse");
                  setFormData(prev => ({ ...prev, sourceWarehouse: "", sourceLocation: "", destinationDefaultWarehouse: "", destinationDefaultLocation: "" }));
                  setTransferItems([]);
                  setAvailableItems([]);
                }}
                className={cn(
                  "flex-1 p-4 rounded-lg border-2 transition-all text-left",
                  transferMode === "warehouse"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
              >
                <div className="font-semibold mb-1">Warehouse Level</div>
                <div className="text-sm text-muted-foreground">
                  Quick transfers between warehouses (aggregated stock)
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setTransferMode("location");
                  setFormData(prev => ({ ...prev, sourceWarehouse: "", sourceLocation: "", destinationDefaultWarehouse: "", destinationDefaultLocation: "" }));
                  setTransferItems([]);
                  setAvailableItems([]);
                }}
                className={cn(
                  "flex-1 p-4 rounded-lg border-2 transition-all text-left",
                  transferMode === "location"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
              >
                <div className="font-semibold mb-1">Location Level</div>
                <div className="text-sm text-muted-foreground">
                  Precise transfers between specific locations (detailed tracking)
                </div>
              </button>
            </div>
          </div>

          {/* Source Warehouse (Warehouse Mode) */}
          {transferMode === "warehouse" && (
            <div className="space-y-2">
              <Label htmlFor="sourceWarehouse">
                Source Warehouse <span className="text-destructive">*</span>
              </Label>
              <Combobox
                options={warehouseOptions}
                value={formData.sourceWarehouse}
                onValueChange={(value) => handleFieldChange("sourceWarehouse", value)}
                placeholder="Select source warehouse..."
                searchPlaceholder="Search warehouses..."
                emptyText="No warehouses found."
                className={cn(
                  formErrors.sourceWarehouse && "border-destructive"
                )}
              />
              {formErrors.sourceWarehouse && (
                <FieldError
                  id="sourceWarehouse-error"
                  message={formErrors.sourceWarehouse}
                />
              )}
            </div>
          )}

          {/* Destination Warehouse (Warehouse Mode) */}
          {transferMode === "warehouse" && (
            <div className="space-y-2">
              <Label htmlFor="destinationDefaultWarehouse">
                Destination Warehouse
              </Label>
              <Combobox
                options={warehouseOptions.filter(w => w.value !== formData.sourceWarehouse)}
                value={formData.destinationDefaultWarehouse}
                onValueChange={(value) => {
                  handleFieldChange("destinationDefaultWarehouse", value);
                  // Apply to all items that don't have a destination set
                  setTransferItems((prev) =>
                    prev.map((item) => ({
                      ...item,
                      destinationWarehouse: item.destinationWarehouse || value,
                    }))
                  );
                }}
                placeholder="Select destination warehouse..."
                searchPlaceholder="Search warehouses..."
                emptyText="No warehouses found."
              />
              <p className="text-sm text-muted-foreground">
                Set a default destination for all items (can be overridden per item)
              </p>
            </div>
          )}

          {/* Source Location (Location Mode) */}
          {transferMode === "location" && (
            <div className="space-y-2">
              <Label htmlFor="sourceLocation">
                Source Location <span className="text-destructive">*</span>
              </Label>
            <Combobox
              options={mockLocations
                .filter((loc) => loc.active)
                .map((location) => {
                  const warehouse = mockWarehouses.find(w => w.code === location.warehouseCode);
                  return {
                    value: location.locationCode,
                    label: location.locationCode,
                    description: `${warehouse?.name || location.warehouseCode} (${location.warehouseCode})`,
                  };
                })}
              value={formData.sourceLocation}
              onValueChange={(value) => handleFieldChange("sourceLocation", value)}
              placeholder="Select source location..."
              searchPlaceholder="Search locations..."
              emptyText="No locations found."
              className={cn(
                formErrors.sourceLocation && "border-destructive"
              )}
            />
            {formErrors.sourceLocation && (
              <FieldError
                id="sourceLocation-error"
                message={formErrors.sourceLocation}
              />
            )}
            </div>
          )}

          {/* Destination Location (Location Mode) */}
          {transferMode === "location" && (
            <div className="space-y-2">
              <Label htmlFor="destinationDefaultLocation">
                Destination Location
              </Label>
              <Combobox
                options={mockLocations
                  .filter((loc) => loc.active && loc.locationCode !== formData.sourceLocation)
                  .map((location) => {
                    const warehouse = mockWarehouses.find(w => w.code === location.warehouseCode);
                    return {
                      value: location.locationCode,
                      label: location.locationCode,
                      description: `${warehouse?.name || location.warehouseCode} (${location.warehouseCode})`,
                    };
                  })}
                value={formData.destinationDefaultLocation}
                onValueChange={(value) => {
                  handleFieldChange("destinationDefaultLocation", value);
                  // Apply to all items that don't have a destination set
                  setTransferItems((prev) =>
                    prev.map((item) => ({
                      ...item,
                      destinationLocation: item.destinationLocation || value,
                    }))
                  );
                }}
                placeholder="Select destination location..."
                searchPlaceholder="Search locations..."
                emptyText="No locations found."
              />
              <p className="text-sm text-muted-foreground">
                Set a default destination for all items (can be overridden per item)
              </p>
            </div>
          )}

          {/* Notes - Full Width */}
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="notes">Transfer Notes</Label>
            <Textarea
              id="notes"
              placeholder="Reason for transfer or additional details"
              maxLength={500}
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFieldChange("notes", e.target.value)}
              aria-invalid={!!formErrors.notes}
              aria-describedby={
                formErrors.notes ? "notes-error" : "notes-count"
              }
              className={cn(formErrors.notes && "border-destructive")}
            />
            <p
              id="notes-count"
              className="text-sm text-muted-foreground text-right"
            >
              {formData.notes.length}/500 characters
            </p>
            {formErrors.notes && (
              <FieldError id="notes-error" message={formErrors.notes} />
            )}
          </div>
        </div>
      </Card>

      {/* Items to Transfer Section */}
      {((transferMode === "warehouse" && formData.sourceWarehouse) || (transferMode === "location" && formData.sourceLocation)) && (
        <Card className="p-6">
          <div className="space-y-4">
            {/* Section Header with Add Button */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">Items to Transfer</h3>
                <p className="text-sm text-muted-foreground">
                  {transferMode === "warehouse" 
                    ? `Add items from ${mockWarehouses.find(w => w.code === formData.sourceWarehouse)?.name || formData.sourceWarehouse} to transfer`
                    : `Add items from location ${formData.sourceLocation} to transfer`
                  }
                </p>
              </div>
              <Button
                onClick={handleOpenItemModal}
                disabled={isLoadingItems || availableItems.length === 0}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Items to Transfer
              </Button>
            </div>

            {/* Loading State */}
            {isLoadingItems && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Loader2 className="h-12 w-12 text-muted-foreground mb-4 animate-spin" />
                <p className="text-muted-foreground">Loading available items...</p>
              </div>
            )}

            {/* Empty State - No items in warehouse */}
            {!isLoadingItems && availableItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">No items available in this warehouse</p>
                <p className="text-sm text-muted-foreground">
                  Try selecting a different source warehouse
                </p>
              </div>
            )}

            {/* Empty State - No items added yet */}
            {!isLoadingItems && availableItems.length > 0 && transferItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">No items added to transfer yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Click "Add Items to Transfer" to select items from this warehouse
                </p>
                <Button
                  onClick={handleOpenItemModal}
                  variant="outline"
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Items
                </Button>
              </div>
            )}

            {/* Items Table */}
            {!isLoadingItems && transferItems.length > 0 && (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[25%]">Stock Item</TableHead>
                      <TableHead className="w-[10%]">Available</TableHead>
                      <TableHead className="w-[12%]">Transfer Qty</TableHead>
                      <TableHead className="w-[10%]">Unit Price</TableHead>
                      <TableHead className="w-[25%]">Destination</TableHead>
                      <TableHead className="w-[13%]">Value</TableHead>
                      <TableHead className="w-[5%]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transferItems.map((item) => {
                      const transferValue = (parseFloat(item.transferQty) || 0) * item.unitPrice;
                      return (
                        <TableRow key={item.stockCode}>
                          {/* Stock Item */}
                          <TableCell>
                            <div>
                              <div className="font-medium">{item.stockCode}</div>
                              <div className="text-sm text-muted-foreground">
                                {item.stockDescription}
                              </div>
                            </div>
                          </TableCell>

                          {/* Available Quantity */}
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {item.availableQty}
                            </Badge>
                          </TableCell>

                          {/* Transfer Quantity */}
                          <TableCell>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                placeholder="0"
                                min="0.01"
                                max={item.availableQty}
                                step="0.01"
                                value={item.transferQty}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.stockCode,
                                    "transferQty",
                                    e.target.value
                                  )
                                }
                                className={cn(
                                  "w-full",
                                  item.errors.transferQty && "border-destructive"
                                )}
                                aria-required="true"
                                aria-invalid={!!item.errors.transferQty}
                              />
                              {item.errors.transferQty && (
                                <FieldError message={item.errors.transferQty} />
                              )}
                            </div>
                          </TableCell>

                          {/* Unit Price */}
                          <TableCell>
                            <div className="font-medium text-muted-foreground">
                              {formatCurrency(item.unitPrice)}
                            </div>
                          </TableCell>

                          {/* Destination Warehouse/Location */}
                          <TableCell>
                            <div className="space-y-1">
                              {transferMode === "warehouse" ? (
                                <>
                                  <Combobox
                                    options={mockWarehouses
                                      .filter((wh) => wh.active && wh.code !== formData.sourceWarehouse)
                                      .map((warehouse) => ({
                                        value: warehouse.code,
                                        label: warehouse.name,
                                        description: warehouse.code,
                                      }))}
                                    value={item.destinationWarehouse}
                                    onValueChange={(value) =>
                                      handleItemChange(
                                        item.stockCode,
                                        "destinationWarehouse",
                                        value
                                      )
                                    }
                                    placeholder="Select warehouse..."
                                    searchPlaceholder="Search warehouses..."
                                    emptyText="No warehouses found."
                                    className={cn(
                                      item.errors.destinationWarehouse && "border-destructive"
                                    )}
                                  />
                                  {item.errors.destinationWarehouse && (
                                    <FieldError message={item.errors.destinationWarehouse} />
                                  )}
                                </>
                              ) : (
                                <>
                                  <Combobox
                                    options={mockLocations
                                      .filter((loc) => loc.active && loc.locationCode !== formData.sourceLocation)
                                      .map((location) => {
                                        const warehouse = mockWarehouses.find(w => w.code === location.warehouseCode);
                                        return {
                                          value: location.locationCode,
                                          label: location.locationCode,
                                          description: `${warehouse?.name || location.warehouseCode} (${location.warehouseCode})`,
                                        };
                                      })}
                                    value={item.destinationLocation}
                                    onValueChange={(value) =>
                                      handleItemChange(
                                        item.stockCode,
                                        "destinationLocation",
                                        value
                                      )
                                    }
                                    placeholder="Select location..."
                                    searchPlaceholder="Search locations..."
                                    emptyText="No locations found."
                                    className={cn(
                                      item.errors.destinationLocation && "border-destructive"
                                    )}
                                  />
                                  {item.errors.destinationLocation && (
                                    <FieldError message={item.errors.destinationLocation} />
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>

                          {/* Transfer Value */}
                          <TableCell>
                            <div className={cn(
                              "font-medium",
                              transferValue > 0 && "text-primary"
                            )}>
                              {transferValue > 0 ? formatCurrency(transferValue) : "—"}
                            </div>
                          </TableCell>

                          {/* Remove Button */}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(item.stockCode)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              aria-label={`Remove ${item.stockDescription}`}
                            >
                              ×
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Transfer Summary */}
      {transferItems.length > 0 && (
        <Card className="p-6 lg:sticky lg:top-4">
          <h3 className="text-lg font-bold mb-4">Transfer Summary</h3>

          {/* Transfer Visualization */}
          <div className="mb-6 p-4 bg-muted/50 rounded-lg space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 text-center">
                <p className="text-sm text-muted-foreground mb-1">From</p>
                {transferMode === "warehouse" ? (
                  <>
                    <p className="font-semibold">
                      {mockWarehouses.find(w => w.code === formData.sourceWarehouse)?.name || formData.sourceWarehouse}
                    </p>
                    <p className="text-xs text-muted-foreground">{formData.sourceWarehouse}</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">{formData.sourceLocation}</p>
                    <p className="text-xs text-muted-foreground">Location</p>
                  </>
                )}
              </div>
              <ArrowRight className="h-6 w-6 text-primary flex-shrink-0" />
              <div className="flex-1 text-center">
                <p className="text-sm text-muted-foreground mb-1">To</p>
                {summary.destinations.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Not specified</p>
                )}
                {summary.destinations.length === 1 && (
                  <>
                    {transferMode === "warehouse" ? (
                      <>
                        <p className="font-semibold">
                          {mockWarehouses.find(w => w.code === summary.destinations[0])?.name || summary.destinations[0]}
                        </p>
                        <p className="text-xs text-muted-foreground">{summary.destinations[0]}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold">{summary.destinations[0]}</p>
                        <p className="text-xs text-muted-foreground">Location</p>
                      </>
                    )}
                  </>
                )}
                {summary.destinations.length > 1 && (
                  <p className="font-semibold">
                    {summary.destinations.length} {transferMode === "warehouse" ? "warehouses" : "locations"}
                  </p>
                )}
              </div>
            </div>
            {summary.destinations.length > 1 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-1">Destinations:</p>
                <div className="flex flex-wrap gap-1">
                  {summary.destinations.map((dest) => {
                    if (transferMode === "warehouse") {
                      const warehouse = mockWarehouses.find(w => w.code === dest);
                      return (
                        <Badge key={dest} variant="secondary" className="text-xs">
                          {warehouse?.name || dest} ({dest})
                        </Badge>
                      );
                    } else {
                      return (
                        <Badge key={dest} variant="secondary" className="text-xs">
                          {dest}
                        </Badge>
                      );
                    }
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Selected Items</span>
              <span className="text-2xl font-bold">{summary.totalItems}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Total Quantity
              </span>
              <span className="text-2xl font-bold">
                {summary.totalQuantity.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-sm text-muted-foreground">Total Value</span>
              <span className="text-3xl font-bold text-primary">
                {formatCurrency(summary.totalValue)}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80 border-t z-50">
        <div className="container max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              {transferItems.length > 0 ? (
                <span>
                  {transferItems.length} item{transferItems.length > 1 ? "s" : ""} to transfer
                </span>
              ) : (
                <span>No items added</span>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={isSaving || transferItems.length === 0}
              >
                {isSaving ? "Saving..." : "Save Transfer"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Item Selection Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Items to Transfer</DialogTitle>
            <DialogDescription>
              Choose items from {mockWarehouses.find(w => w.code === formData.sourceLocation)?.name || formData.sourceLocation} to add to your transfer
            </DialogDescription>
          </DialogHeader>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by stock code or description..."
              value={itemSearchQuery}
              onChange={(e) => setItemSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={getFilteredAvailableItems().length > 0 && getFilteredAvailableItems().every(item => tempSelectedItems.has(item.stockCode))}
                      onCheckedChange={handleModalSelectAll}
                      aria-label="Select all filtered items"
                    />
                  </TableHead>
                  <TableHead>Stock Item</TableHead>
                  <TableHead className="w-[120px]">Available Qty</TableHead>
                  <TableHead className="w-[120px]">Unit Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredAvailableItems().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {itemSearchQuery ? "No items match your search" : "No items available"}
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredAvailableItems().map((item) => (
                    <TableRow 
                      key={item.stockCode}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        tempSelectedItems.has(item.stockCode) && "bg-muted/50"
                      )}
                      onClick={() => handleModalItemToggle(item.stockCode)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={tempSelectedItems.has(item.stockCode)}
                          onCheckedChange={() => handleModalItemToggle(item.stockCode)}
                          aria-label={`Select ${item.stockDescription}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.stockCode}</div>
                          <div className="text-sm text-muted-foreground">
                            {item.stockDescription}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {item.availableQty}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatCurrency(item.unitPrice)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {tempSelectedItems.size} item(s) selected
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowItemModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmItemSelection}
                disabled={tempSelectedItems.size === 0}
              >
                Add {tempSelectedItems.size} Item(s)
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Transfer?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigateTo("stock-movements")}
              className="bg-destructive hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
